import { Volume } from '@/api/type-server'
import { Progress, Trans } from '@/components'
import { useUserStore } from '@/stores/user'
import { formatGB, formatStorageGB, getEffectiveQuota, toBytes } from '@/utils/storage'
import { cn } from '@/utils/style'

interface VolumeSectionProps {
  volumes: Volume[]
  isExpired?: boolean // Override internal userStore.isOrderExpired
  warningMessages?: {
    low?: string // usage >= 0.85 && usage < 1
    critical?: string // usage >= 1 or quota <= 0
  }
}

export default function SnapVolumeSection({
  volumes,
  isExpired: isExpiredProp,
  warningMessages,
}: VolumeSectionProps) {
  const userStore = useUserStore()

  if (volumes.length === 0) {
    volumes[0] = {
      usage_mb: 0,
      quota_gb: 0,
      init_quota_gb: 0,
      last_quota_gb: 0,
      type: 2,
      domain: 0,
      id: -1,
    }
  }

  const isExpired = isExpiredProp ?? userStore.isOrderExpired

  return (
    <div>
      <h3 className="t-sb text-primary mb-4">
        <Trans i18nKey="space.volumeItemTitleSnapshot">Snapshot</Trans>
      </h3>
      <div className="gap-4">
        {volumes.map((volume, index) => {
          const usageBytes = volume.usage_bytes ?? toBytes(volume.usage_mb, 'mb')
          const effectiveQuota =
            usageBytes > 0
              ? getEffectiveQuota(volume, isExpired)
              : isExpired
                ? volume.last_quota_gb || 0
                : volume.quota_gb || 0
          const usage = effectiveQuota > 0 ? volume.usage_mb / 1024 / effectiveQuota : 0
          return (
            <div
              key={`snap.${volume.id}.${index}`}
              className="w-full p-6 flex rounded-2xl gap-3 items-start border border-neutral-300"
            >
              <div className="w-[120px]">
                <div className={cn(' text-[20px] leading-7 font-bold')}>
                  {`${effectiveQuota ? formatGB(effectiveQuota, { decimals: 0 }) : volume.last_quota_gb > 0 ? '0 GB' : '-'}`}
                </div>
                <div className=" t-sm text-secondary">
                  <Trans i18nKey="space.volumeItemSubTitleStorage">Storage</Trans>
                </div>
              </div>
              <div className="w-full">
                <div className="py-[10px]">
                  <Progress
                    color={cn({
                      'bg-tertiary': isExpired,
                      'bg-error': !isExpired && usage === 1,
                      'bg-warning': !isExpired && usage >= 0.85 && usage < 1,
                      'bg-btn-tertiary': !isExpired && usage < 0.85,
                    })}
                    value={usage}
                  />
                </div>

                {volume && effectiveQuota > 0 && (
                  <div className="flex gap-x-1 t-sm">
                    <>
                      <Trans
                        i18nKey="space.volumeItemUsage"
                        values={{
                          usage: formatStorageGB(toBytes(volume.usage_mb, 'mb')),
                          quota: formatGB(effectiveQuota, { decimals: 0 }),
                        }}
                        components={[
                          <span className="t-sm text-secondary">Using </span>,
                          <span className="t-sm text-secondary"> of </span>,
                        ]}
                      >
                        Using {{ usage: formatStorageGB(toBytes(volume.usage_mb, 'mb')) }}GB of{' '}
                        {{ quota: formatGB(effectiveQuota, { decimals: 0 }) }}GB
                      </Trans>

                      <div className="t-sm text-tertiary">
                        &nbsp;&nbsp;{`${(usage * 100).toFixed(2) || '0'}`}%
                      </div>
                    </>
                  </div>
                )}

                {!isExpired &&
                  volume.quota_gb === 0 &&
                  volume.last_quota_gb > 0 &&
                  (volume.usage_bytes ?? toBytes(volume.usage_mb, 'mb')) > 0 && (
                    <div className="t-sm text-warning">
                      <Trans i18nKey="space.quotaReallocationNotice">
                        Quota needs to be re-allocated – please modify your quota settings.
                      </Trans>
                    </div>
                  )}

                {usage >= 0.85 && usage < 1 && (
                  <div className="flex gap-x-1 t-sm text-warning">
                    {warningMessages?.low || (
                      <Trans i18nKey="space.volumeItemLowStorage">
                        Low storage – manage your storage to avoid interruption
                      </Trans>
                    )}
                  </div>
                )}

                {(!volume || effectiveQuota <= 0 || usage >= 1) && (
                  <div className={cn('t-sm ', usage >= 1 ? 'text-error' : 'text-tertiary')}>
                    {warningMessages?.critical || (
                      <Trans i18nKey="space.volumeItemNotAvailable">
                        No storage available – adjust your quota to continue
                      </Trans>
                    )}
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
